import { Fragment, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  getFinancialReviewDetail,
  listAdminDeposits,
  listFinancialReviews,
  postDirectRefund,
  resolveFinancialReview,
  type AdminDepositsResponse,
  type FinancialReviewDetailResponse,
  type FinancialReviewListResponse,
  type FinancialReviewResolveResponse,
  type ResolveReviewInput,
  type WireFinancialResolution,
  type WireRefundReason,
  type WireReviewSource,
  type WireReviewStatus,
} from '../api/endpoints.js';
import { useApiMutation, useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  IdMono,
  Input,
  LoadingState,
  Modal,
  PageHead,
  Select,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';
import type { BadgeTone } from '../ui/index.js';
import '../styles/financial.mobile.css';

const STATUS_FILTERS: readonly WireReviewStatus[] = ['open', 'escalated', 'resolved', 'rejected'];
const SOURCE_FILTERS: readonly WireReviewSource[] = [
  'reconciliation_mismatch',
  'payment_dispute',
  'refund_approval',
  'delivery_failure',
];
const RESOLUTIONS: readonly WireFinancialResolution[] = [
  'no_action', 'refund_posted', 'credit_issued', 'adjustment_posted', 'rejected', 'escalated',
];
const REFUND_REASONS: readonly WireRefundReason[] = [
  'customer_request', 'operator_goodwill', 'chargeback_received',
  'duplicate_charge', 'delivery_failure', 'review_resolution',
];

function statusTone(s: WireReviewStatus | string): BadgeTone {
  if (s === 'open' || s === 'escalated') return 'warn';
  if (s === 'resolved') return 'success';
  if (s === 'rejected') return 'danger';
  return 'neutral';
}

/**
 * Server codes here are long snake_case tokens (methodId, source, reason). A
 * <wbr> after each "_" lets a code wrap at a word boundary in a narrow column
 * or phone card instead of mid-word; the text content (and copy) is unchanged.
 * The single <span> keeps the code one flex item inside flex cells/badges.
 */
function breakableCode(code: string): ReactNode {
  const parts = code.split('_');
  if (parts.length === 1) return code;
  return (
    <span>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {part}
          {i < parts.length - 1 ? (
            <>
              _<wbr />
            </>
          ) : null}
        </Fragment>
      ))}
    </span>
  );
}

/**
 * Same idea for a deposit's user (e-mail / username): a <wbr> before each "."
 * and "@" and after each "_", so a long address wraps at its punctuation
 * instead of mid-word. Text content (and copy) is unchanged.
 */
function breakableIdentity(text: string): ReactNode {
  const parts: string[] = [];
  let current = '';
  for (const ch of text) {
    if ((ch === '.' || ch === '@') && current !== '') {
      parts.push(current);
      current = ch;
    } else if (ch === '_') {
      parts.push(current + ch);
      current = '';
    } else {
      current += ch;
    }
  }
  if (current !== '') parts.push(current);
  if (parts.length <= 1) return text;
  return (
    <span>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {part}
        </Fragment>
      ))}
    </span>
  );
}

/**
 * View-only: after "Aç" the case detail renders below the whole case list —
 * on a phone / tablet (where the list is stacked cards, see
 * styles/financial.mobile.css) that is several screens under the tapped card.
 * On those screens, scroll it into view when its top is not already on screen
 * (the sticky header offset comes from the wrapper's scroll-margin-top).
 * Mouse desktops (≥1280px) keep the old behaviour. No state or data flow is
 * involved.
 */
const REVEAL_DETAIL_MEDIA = '(max-width: 1279px), (hover: none) and (pointer: coarse)';

/**
 * An ISO timestamp ("2026-09-23T10:44:12.123Z") with its only line-break
 * opportunity before the time part: a narrow column shows the date over the
 * time instead of breaking inside the date ("2026-09-" / "23T10:44…"). The
 * text is unchanged.
 */
function breakBeforeTime(iso: string): ReactNode {
  const t = iso.indexOf('T');
  if (t <= 0) return iso;
  // One wrapper: in a stacked (flex) cell the <wbr> must not split the text
  // into two flex items.
  return (
    <span>
      {iso.slice(0, t)}
      <wbr />
      {iso.slice(t)}
    </span>
  );
}

/**
 * View-only: a failure message that appears under the button the operator
 * just pressed (the end of the page) is scrolled into view, instead of
 * landing below the bottom edge of the screen while nothing visible changed.
 */
function revealOnMount(el: HTMLElement | null): void {
  el?.scrollIntoView?.({ block: 'nearest' });
}

function revealIfOffscreen(el: HTMLElement | null): void {
  if (!el || typeof el.scrollIntoView !== 'function') return;
  if (typeof window.matchMedia !== 'function' || !window.matchMedia(REVEAL_DETAIL_MEDIA).matches) return;
  const top = el.getBoundingClientRect().top;
  const viewport = window.innerHeight || document.documentElement.clientHeight;
  const headerOffset = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
  if (top >= headerOffset - 16 && top <= viewport * 0.6) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
}

function refundTone(s: string): BadgeTone {
  if (s === 'posted') return 'success';
  if (s === 'pending') return 'warn';
  if (s === 'failed') return 'danger';
  return 'neutral';
}

export function FinancialReviewsPage(): JSX.Element {
  const canRead = useCan('financial:review');
  const canRefund = useCan('financial:refund');
  const [tab, setTab] = useState<'deposits' | 'reviews'>('deposits');
  const [statusFilter, setStatusFilter] = useState<WireReviewStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState<WireReviewSource | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  // View-only: every "Aç" tap (also on the already-open case) asks the detail
  // wrapper to scroll into view once it has rendered.
  const detailRef = useRef<HTMLDivElement>(null);
  const [revealTick, setRevealTick] = useState(0);
  useEffect(() => {
    if (revealTick > 0) revealIfOffscreen(detailRef.current);
  }, [revealTick]);
  // View-only: a resolved case closes its detail (often ~2000px down a phone
  // list) and the queue reloads; bring the tab row + refreshed list back into
  // view instead of leaving the operator at the bottom of a shrunk page.
  const tabsRef = useRef<HTMLDivElement>(null);
  const [listRevealTick, setListRevealTick] = useState(0);
  useEffect(() => {
    if (listRevealTick > 0) revealIfOffscreen(tabsRef.current);
  }, [listRevealTick]);

  const list = useApiQuery<FinancialReviewListResponse>(
    (o) =>
      listFinancialReviews(o, {
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        limit: 50,
      }),
    [statusFilter, sourceFilter],
  );

  return (
    <div className="finrev-page">
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Ödeme İncelemeleri</>}
        title="Ödeme İncelemeleri"
        subtitle="Mutabakat uyumsuzlukları, ödeme itirazları, iade onayları ve teslimat başarısızlıkları için operatör kuyruğu. Reconciler bir kayıt açar; ledger sessizce uyumsuzluğu kapatmaz."
        actions={
          canRefund ? (
            <Button variant="primary" icon={<Icon.Coins size={14} />} onClick={() => setShowRefundModal(true)}>
              İade başlat
            </Button>
          ) : null
        }
      />

      {!canRead ? <ErrorState reason="capability_missing:financial:review" /> : null}

      {canRead ? (
        <>
          <div className="finrev-tabs" ref={tabsRef} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <Button
              variant={tab === 'deposits' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setTab('deposits')}
            >
              Para Yatırmalar
            </Button>
            <Button
              variant={tab === 'reviews' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setTab('reviews')}
            >
              Manuel İnceleme
            </Button>
          </div>

          {tab === 'deposits' ? <DepositsTab /> : null}

          {tab === 'reviews' ? (
          <>
          <Card>
            <div className="finrev-filters" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="finrev-filter">
                <label htmlFor="rev-status" className="field-label">Status</label>
                <Select
                  id="rev-status"
                  className="finrev-select finrev-select-180"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as WireReviewStatus | '')}
                >
                  <option value="">outstanding</option>
                  {STATUS_FILTERS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </div>
              <div className="finrev-filter">
                <label htmlFor="rev-source" className="field-label">Source</label>
                <Select
                  id="rev-source"
                  className="finrev-select finrev-select-230"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value as WireReviewSource | '')}
                >
                  <option value="">any</option>
                  {SOURCE_FILTERS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </div>
              <Button className="finrev-refresh" variant="secondary" icon={<Icon.Refresh size={14} />} onClick={() => list.reload()}>
                Yenile
              </Button>
            </div>
          </Card>

          {list.kind === 'loading' ? <LoadingState /> : null}
          {list.kind === 'error' ? <ErrorState reason={list.reason} onRetry={list.reload} /> : null}
          {list.kind === 'ok' ? (
            <ReviewList
              entries={list.data.entries}
              selectedId={selectedId}
              onSelect={(id) => {
                setSelectedId(id);
                setRevealTick((n) => n + 1);
              }}
            />
          ) : null}

          {selectedId ? (
            <div ref={detailRef} className="finrev-detail" style={{ marginTop: 16 }}>
              <ReviewDetail
                reviewCaseId={selectedId}
                canRefund={canRefund}
                onResolved={() => {
                  setSelectedId(null);
                  list.reload();
                  setListRevealTick((n) => n + 1);
                }}
              />
            </div>
          ) : null}
          </>
          ) : null}
        </>
      ) : null}

      <Modal
        open={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          list.reload();
        }}
        title="Direct refund"
        width={620}
        testId="direct-refund-modal"
      >
        <DirectRefundBody onDone={() => {
          setShowRefundModal(false);
          list.reload();
        }} />
      </Modal>
    </div>
  );
}

function ReviewList({
  entries,
  selectedId,
  onSelect,
}: {
  readonly entries: FinancialReviewListResponse['entries'];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}): JSX.Element {
  if (entries.length === 0) {
    return (
      <Card>
        <EmptyState
          label="Bu filtreyle eşleşen inceleme kaydı yok."
          hint="Daha fazla görmek için durum veya kaynak filtresini değiştirin."
        />
      </Card>
    );
  }
  return (
    <Card flush>
      <CardHeader title={`Cases — ${entries.length}`} />
      <CardBody tight style={{ padding: 0 }}>
        <Table dense stack className="tbl-stack-xl finrev-reviews">
          <THead>
            <TRow>
              <TH>Opened</TH>
              <TH>Source</TH>
              <TH>Status</TH>
              <TH>Reason</TH>
              <TH>Checkout</TH>
              <TH>Actor</TH>
              <TH></TH>
            </TRow>
          </THead>
          <TBody>
            {entries.map((e) => (
              <TRow key={e.reviewCaseId} className={selectedId === e.reviewCaseId ? 'active' : ''}>
                <TD label="Opened" className="t-mono t-xs finrev-opened">{breakBeforeTime(e.openedAt)}</TD>
                <TD label="Source" className="t-mono t-xs finrev-code tbl-stack-primary">{breakableCode(e.source)}</TD>
                <TD label="Status" className="tbl-stack-status"><Badge tone={statusTone(e.status)} dot>{e.status}</Badge></TD>
                <TD label="Reason" className="t-mono t-xs finrev-code tbl-stack-full">{breakableCode(e.reason)}</TD>
                <TD label="Checkout">{e.checkoutIntentId ? <IdMono value={e.checkoutIntentId} /> : '—'}</TD>
                <TD label="Actor">{e.actorId ? <IdMono value={e.actorId} /> : '—'}</TD>
                <TD className="tbl-stack-actions">
                  <Button variant="ghost" size="sm" onClick={() => onSelect(e.reviewCaseId)} iconRight={<Icon.ChevronRight size={12} />}>
                    Aç
                  </Button>
                </TD>
              </TRow>
            ))}
          </TBody>
        </Table>
      </CardBody>
    </Card>
  );
}

function ReviewDetail({
  reviewCaseId,
  canRefund,
  onResolved,
}: {
  readonly reviewCaseId: string;
  readonly canRefund: boolean;
  readonly onResolved: () => void;
}): JSX.Element {
  const detail = useApiQuery<FinancialReviewDetailResponse>(
    (o) => getFinancialReviewDetail(o, reviewCaseId),
    [reviewCaseId],
  );
  if (detail.kind === 'loading') return <LoadingState />;
  if (detail.kind === 'error') {
    return <ErrorState reason={detail.reason} onRetry={detail.reload} />;
  }
  if (detail.kind !== 'ok') return <></>;
  const { case: c, financial, refunds } = detail.data;

  return (
    <Card flush>
      <CardHeader
        title={<>Case <IdMono value={c.reviewCaseId} /></>}
        actions={<Badge tone={statusTone(c.status)} dot>{c.status}</Badge>}
      />
      <CardBody>
        <div className="dl-rows">
          <Row k="Source" v={<span className="t-mono">{breakableCode(c.source)}</span>} />
          <Row k="Reason" v={<span className="t-mono">{breakableCode(c.reason)}</span>} />
          <Row k="Detail" v={<span className="t-mono">{c.detail ?? '—'}</span>} />
          <Row k="Checkout" v={c.checkoutIntentId ? <IdMono value={c.checkoutIntentId} /> : '—'} />
          <Row k="Actor" v={c.actorId ? <IdMono value={c.actorId} /> : '—'} />
          <Row k="Opened at" v={<span className="t-mono t-xs">{c.openedAt}</span>} />
          {c.resolvedAt ? (
            <>
              <Row k="Resolved at" v={<span className="t-mono t-xs">{c.resolvedAt}</span>} />
              <Row k="Resolved by" v={<span className="t-mono">{c.resolvedByActorId != null ? breakableCode(c.resolvedByActorId) : '—'}</span>} />
              <Row k="Resolution" v={<span className="t-mono">{c.resolution != null ? breakableCode(c.resolution) : '—'}</span>} />
              <Row k="Note" v={c.resolutionNote ?? '—'} />
            </>
          ) : null}
        </div>

        {financial ? (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Financial context</h3>
            <div className="dl-rows">
              <Row k="Financial state" v={<span className="t-mono">{breakableCode(financial.financialState)}</span>} />
              <Row k="Paid (minor)" v={<span className="t-mono t-num">{financial.paidAmountMinor}</span>} />
              <Row k="Refunded (minor)" v={<span className="t-mono t-num">{financial.refundedAmountMinor}</span>} />
              <Row k="Currency" v={<span className="t-mono">{financial.currency}</span>} />
              <Row k="Ledger entries" v={<span className="t-mono t-xs finrev-ledger-ids">{financial.ledgerEntryIds.join(', ') || '—'}</span>} />
            </div>
          </div>
        ) : null}

        {refunds.length > 0 ? (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Bağlı iade istekleri</h3>
            {/* lang="en": the column labels are English; uppercased under the
                document's lang="tr" "Decided" rendered as "DECİDED". */}
            <Table dense stack className="tbl-stack-lg finrev-refunds" lang="en">
              <THead>
                <TRow><TH>ID</TH><TH>Status</TH><TH>Amount</TH><TH>Reason</TH><TH>Decided</TH></TRow>
              </THead>
              <TBody>
                {refunds.map((r) => (
                  <TRow key={r.refundRequestId}>
                    <TD label="ID" className="tbl-stack-primary"><IdMono value={r.refundRequestId} /></TD>
                    <TD label="Status" className="tbl-stack-status"><Badge tone={refundTone(r.status)} dot>{r.status}</Badge></TD>
                    <TD label="Amount" className="t-mono t-num">{r.amountMinor} {r.currency}</TD>
                    <TD label="Reason" className="t-mono t-xs finrev-code">{breakableCode(r.reason)}</TD>
                    <TD label="Decided" className="t-mono t-xs cell-wrap">{r.decidedAt ?? '—'}</TD>
                  </TRow>
                ))}
              </TBody>
            </Table>
          </div>
        ) : null}

        {c.status === 'open' || c.status === 'escalated' ? (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
            <ResolveForm
              reviewCaseId={c.reviewCaseId}
              canRefund={canRefund}
              currency={financial?.currency ?? 'USD'}
              onResolved={onResolved}
            />
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function ResolveForm({
  reviewCaseId,
  canRefund,
  currency,
  onResolved,
}: {
  readonly reviewCaseId: string;
  readonly canRefund: boolean;
  readonly currency: string;
  readonly onResolved: () => void;
}): JSX.Element {
  const [resolution, setResolution] = useState<WireFinancialResolution>('no_action');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [refundReason, setRefundReason] = useState<WireRefundReason>('review_resolution');
  const mutation = useApiMutation<
    { readonly reviewCaseId: string; readonly input: ResolveReviewInput },
    FinancialReviewResolveResponse
  >((o, x) => resolveFinancialReview(o, x.reviewCaseId, x.input));

  const requiresRefund = resolution === 'refund_posted';
  const canSubmit = !requiresRefund || (canRefund && amount !== '' && Number(amount) > 0);

  async function onSubmit(): Promise<void> {
    await mutation.run({
      reviewCaseId,
      input: {
        resolution,
        resolutionNote: note || null,
        refund: requiresRefund
          ? {
              amountMinor: Math.trunc(Number(amount)),
              currency,
              reason: refundReason,
              memo: note || null,
            }
          : undefined,
      },
    });
  }

  if (mutation.state.kind === 'ok') {
    onResolved();
    return <Banner tone="success">Resolved.</Banner>;
  }

  return (
    <div>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Resolve</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Resolution" htmlFor="resolution">
          <Select id="resolution" value={resolution} onChange={(e) => setResolution(e.target.value as WireFinancialResolution)}>
            {RESOLUTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Select>
        </Field>
        <Field label="Note" htmlFor="note">
          <Input id="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="operator-safe note" />
        </Field>
        {requiresRefund ? (
          <>
            <Field label="Refund amount (minor)" htmlFor="amt" help={`e.g. 500 = ${currency} 5.00`}>
              <Input
                id="amt"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="numeric"
              />
            </Field>
            <Field label="Refund reason" htmlFor="rfreason">
              <Select id="rfreason" value={refundReason} onChange={(e) => setRefundReason(e.target.value as WireRefundReason)}>
                {REFUND_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </Select>
            </Field>
          </>
        ) : null}
        <div className="finrev-resolve-submit">
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.state.kind === 'running'}
            onClick={() => void onSubmit()}
            icon={<Icon.Check size={14} />}
          >
            Apply resolution
          </Button>
          {mutation.state.kind === 'error' ? (
            <div style={{ marginTop: 8 }} ref={revealOnMount}>
              <Banner tone="danger">{mutation.state.reason}</Banner>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DirectRefundBody({ onDone }: { readonly onDone: () => void }): JSX.Element {
  const [checkoutIntentId, setCheckoutIntentId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reason, setReason] = useState<WireRefundReason>('operator_goodwill');
  const [memo, setMemo] = useState('');
  const mutation = useApiMutation(postDirectRefund);

  async function onSubmit(): Promise<void> {
    await mutation.run({
      checkoutIntentId,
      amountMinor: Math.trunc(Number(amount)),
      currency,
      reason,
      memo: memo || null,
    });
  }

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)', margin: '0 0 16px', fontSize: 13.5 }}>
        Admin-direct refund. Creates a refund request AND posts the ledger row in one step.
        For reviewable cases use the review queue instead.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Checkout intent" htmlFor="ref-ci">
          <Input id="ref-ci" value={checkoutIntentId} onChange={(e) => setCheckoutIntentId(e.target.value)} />
        </Field>
        <Field label="Amount (minor)" htmlFor="ref-amt">
          <Input id="ref-amt" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Currency" htmlFor="ref-cur">
          <Input id="ref-cur" value={currency} onChange={(e) => setCurrency(e.target.value)} />
        </Field>
        <Field label="Reason" htmlFor="ref-reason">
          <Select id="ref-reason" value={reason} onChange={(e) => setReason(e.target.value as WireRefundReason)}>
            {REFUND_REASONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </Select>
        </Field>
        <Field label="Memo" htmlFor="ref-memo">
          <Input id="ref-memo" value={memo} onChange={(e) => setMemo(e.target.value)} />
        </Field>
        {/* Actions + result share one wrapper: on a phone it is the sheet's
            sticky footer, so the posted/error banner shows next to the
            button that produced it (styles/financial.mobile.css). */}
        <div className="finrev-refund-footer">
          <div className="finrev-refund-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onDone}>İptal</Button>
            <Button
              variant="primary"
              loading={mutation.state.kind === 'running'}
              onClick={() => void onSubmit()}
              icon={<Icon.Send size={14} />}
            >
              Post refund
            </Button>
          </div>
          {mutation.state.kind === 'error' ? (
            <Banner tone="danger">{mutation.state.reason}</Banner>
          ) : null}
          {mutation.state.kind === 'ok' ? (
            <Banner tone="success">
              Posted. Ledger entry <code>{mutation.state.data.ledgerEntryId}</code>.
            </Banner>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function depositTone(s: string): BadgeTone {
  if (s === 'completed') return 'success';
  if (s === 'pending_payment') return 'warn';
  if (s === 'failed') return 'danger';
  return 'neutral';
}

/** e-mail, else username, else display name, else the actor id chip. */
function depositUser(d: AdminDepositsResponse['entries'][number]): ReactNode {
  const who = d.user?.email ?? d.user?.username ?? d.user?.displayName;
  return who != null ? breakableIdentity(who) : <IdMono value={d.actorId} />;
}

const DEPOSIT_STATUSES = [
  'pending_payment',
  'completed',
  'failed',
  'expired',
  'cancelled',
] as const;

/**
 * Para Yatırmalar — V1 deposit-history parity. Lists EVERY user who created a
 * top-up / webhook deposit (customer_topup_intents joined to users). Default
 * status filter is ALL (every deposit today is pending_payment; defaulting to
 * `completed` would reproduce the old empty page). amountUsd is genuine cents.
 */
function DepositsTab(): JSX.Element {
  const [status, setStatus] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const q = useApiQuery<AdminDepositsResponse>(
    (o) => listAdminDeposits(o, { status: status || undefined, limit, offset }),
    [status, offset],
  );

  return (
    <>
      <Card>
        <div className="finrev-filters" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="finrev-filter">
            <label htmlFor="dep-status" className="field-label">Durum</label>
            <Select
              id="dep-status"
              className="finrev-select finrev-select-200"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setOffset(0);
              }}
            >
              <option value="">tümü</option>
              {DEPOSIT_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </div>
          <Button className="finrev-refresh" variant="secondary" icon={<Icon.Refresh size={14} />} onClick={() => q.reload()}>
            Yenile
          </Button>
        </div>
      </Card>

      {q.kind === 'loading' ? <LoadingState /> : null}
      {q.kind === 'error' ? <ErrorState reason={q.reason} onRetry={q.reload} /> : null}
      {q.kind === 'ok' && q.data.entries.length === 0 ? (
        <Card>
          <EmptyState
            label="Bu filtreyle para yatırma kaydı yok."
            hint="Webhook oluşturan / yatırım yapan tüm kullanıcılar burada listelenir."
          />
        </Card>
      ) : null}
      {q.kind === 'ok' && q.data.entries.length > 0 ? (
        <Card flush>
          <CardHeader title={`Para yatırmalar — ${q.data.total}`} />
          <CardBody tight style={{ padding: 0 }}>
            <Table dense stack className="tbl-stack-xl finrev-deposits">
              <THead>
                <TRow>
                  <TH>Tarih</TH>
                  <TH>Kullanıcı</TH>
                  <TH>Tutar</TH>
                  <TH>Yöntem</TH>
                  <TH>Sağlayıcı</TH>
                  <TH className="finrev-col-status">Durum</TH>
                  <TH>Ref</TH>
                  <TH>Ödeme ekranı</TH>
                </TRow>
              </THead>
              <TBody>
                {q.data.entries.map((d) => (
                  <TRow key={d.id}>
                    <TD label="Tarih" className="t-mono t-xs">{new Date(d.createdAt).toLocaleString()}</TD>
                    <TD label="Kullanıcı" className="cell-wrap tbl-stack-primary">
                      {depositUser(d)}
                    </TD>
                    <TD label="Tutar" className="t-mono t-num finrev-amount">
                      ${d.amountUsd.toFixed(2)} {d.currency}
                    </TD>
                    <TD label="Yöntem" className="t-mono t-xs finrev-code">{breakableCode(d.methodId)}</TD>
                    <TD label="Sağlayıcı" className="t-mono t-xs">{d.providerName ?? '—'}</TD>
                    <TD label="Durum" className="tbl-stack-status">
                      <Badge tone={depositTone(d.status)} dot className="finrev-status-badge">{breakableCode(d.status)}</Badge>
                    </TD>
                    <TD label="Ref">{d.providerIntentId ? <IdMono value={d.providerIntentId} /> : '—'}</TD>
                    <TD label="Ödeme ekranı" className={d.redirectUrl ? 'tbl-stack-actions' : undefined}>
                      {d.redirectUrl ? (
                        <a
                          href={d.redirectUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary btn-sm"
                          title="Cryptomus ödeme ekranını yeni sekmede aç"
                        >
                          Ödeme ekranı ↗
                        </a>
                      ) : (
                        '—'
                      )}
                    </TD>
                  </TRow>
                ))}
              </TBody>
            </Table>
          </CardBody>
        </Card>
      ) : null}
      {q.kind === 'ok' && q.data.total > limit ? (
        <div className="finrev-pager" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset <= 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
          >
            Önceki
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset + limit >= q.data.total}
            onClick={() => setOffset(offset + limit)}
          >
            Sonraki
          </Button>
        </div>
      ) : null}
    </>
  );
}

function Row({ k, v }: { readonly k: string; readonly v: React.ReactNode }): JSX.Element {
  return (
    <div className="dl-row">
      <div className="dl-key">{k}</div>
      <div className="dl-val">{v}</div>
    </div>
  );
}
